import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TopBar from '../TopBar/TopBar';
import BottomNav from '../BottomNav/BottomNav';
import { useI18n } from '../../i18n/I18nContext';
import { useBottomNavHidden, useContentGutter, useIsCompact, useShellInsets } from './shellState';
import './Layout.css';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Screens that pin their own control to the bottom edge: the tab bar steps aside there. A screen
// the route cannot tell apart (an open conversation) calls useHideBottomNav() itself.
function pinsBottomEdge(pathname, search) {
  if (pathname === '/courses/create') return true;
  if (/^\/courses\/edit\/[^/]+\/?$/.test(pathname)) return new URLSearchParams(search).get('tab') === 'settings';
  return false;
}

const visible = (el) => el.getClientRects().length > 0;

/**
 * The shell around every signed-in screen: rail, top bar, page column and, below 1024px, the
 * drawer, its scrim and the tab bar. The staff and teacher layouts both render this, each with
 * its own rail and destinations.
 *
 * sidebar: ({ mobileOpen, onMobileClose }) => the rail element
 * nav:     { tabs, hasMore } — the tab bar's destinations (useStaffNav / useTeacherNav)
 */
function AppShell({ sidebar, nav, children }) {
  const { t } = useI18n();
  const { pathname, search } = useLocation();
  const layoutRef = useRef(null);
  const openerRef = useRef(null);
  const openedAtRef = useRef(pathname);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const compact = useIsCompact();
  const tabBarHidden = useBottomNavHidden();
  const showTabBar = compact && !tabBarHidden && !pinsBottomEdge(pathname, search);

  useShellInsets({ top: compact, bottom: showTabBar });
  useContentGutter(layoutRef);

  // Whoever opens it (the pill's menu button, the tab bar's "More") gets focus back on close. The
  // clicked button is used, not document.activeElement: Safari does not focus a button on click.
  const openDrawer = useCallback((event) => {
    const clicked = event && event.currentTarget;
    openerRef.current = clicked && typeof clicked.focus === 'function' ? clicked : document.activeElement;
    openedAtRef.current = pathRef.current;
    setMobileNavOpen(true);
  }, []);
  const closeDrawer = useCallback(() => setMobileNavOpen(false), []);

  // Widening the window past 1024px (or rotating a tablet) turns the drawer back into the rail:
  // nothing may stay open, locked or dimmed.
  useEffect(() => {
    if (!compact) setMobileNavOpen(false);
  }, [compact]);

  // While the drawer is open: the page behind it does not scroll and is out of reach (keyboard,
  // screen reader, taps), Escape closes it, Tab stays inside it, and closing hands focus back to
  // the button that opened it.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const behind = layoutRef.current ? Array.from(layoutRef.current.querySelectorAll('.main-content, .bnav')) : [];
    behind.forEach((el) => { el.inert = true; });

    const drawer = layoutRef.current ? layoutRef.current.querySelector('.sidebar') : null;
    const focusables = () => (drawer ? Array.from(drawer.querySelectorAll(FOCUSABLE)).filter(visible) : []);
    const [first] = focusables();
    if (first) first.focus({ preventScroll: true });

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !drawer) return;
      const list = focusables();
      if (!list.length) return;
      const active = document.activeElement;
      const outside = !drawer.contains(active);
      if (event.shiftKey && (active === list[0] || outside)) {
        event.preventDefault();
        list[list.length - 1].focus();
      } else if (!event.shiftKey && (active === list[list.length - 1] || outside)) {
        event.preventDefault();
        list[0].focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      behind.forEach((el) => { el.inert = false; });
      document.removeEventListener('keydown', onKeyDown);
      const opener = openerRef.current;
      openerRef.current = null;
      // Choosing a destination also closes the drawer; then focus belongs to the new page.
      if (opener && pathRef.current === openedAtRef.current && opener.isConnected && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
    };
  }, [mobileNavOpen]);

  return (
    <div ref={layoutRef} className={`layout${mobileNavOpen ? ' mobile-nav-open' : ''}`}>
      {sidebar({ mobileOpen: mobileNavOpen, onMobileClose: closeDrawer })}
      {mobileNavOpen && (
        <button
          type="button"
          className="sidebar-scrim"
          aria-label={t('ui.close')}
          onClick={closeDrawer}
        />
      )}
      <div className="main-content">
        <TopBar onMenuOpen={openDrawer} />
        <main id="shell-main" className="content-area">
          {children}
        </main>
      </div>
      {showTabBar && <BottomNav tabs={nav.tabs} showMore={nav.hasMore} onMore={openDrawer} />}
    </div>
  );
}

export default AppShell;
